import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, getDaysSectionConfigs, DaysSectionConfig } from '../services/authService';
import DailyNotesService, { DailyNote } from '../services/dailyNotesService';
import BlockService, { ContentBlock } from '../services/blockService';
import { habitService, Habit, HabitCompletion, RecordCompletionData, HabitScore } from '../services/habitService';
import { taskService, Task } from '../services/taskService';
import { tagService, Tag } from '../services/tagService';
import { projectService } from '../services/projectService';
import { Project, ProjectStatus } from '../types/Project';
import TagSelector from './tasks/TagSelector';
import TaskRow from './tasks/TaskRow';
import BlockEditor from './notes/BlockEditor';
import BlockEditorErrorBoundary from './notes/BlockEditorErrorBoundary';
import { useBlockManager } from '../hooks/useBlockManager';
import { BlockContainer } from '../services/blockManager';
import PageHeader from './common/PageHeader';
import { HabitSection } from './habits';
import CompletionValueModal from './habits/CompletionValueModal';
import { KnowledgeCategoryService, KnowledgeCategory } from '../services/knowledgeCategoryService';
import { NovelService, Novel } from '../services/novelService';
import { bookService } from '../services/bookService';
import { Book } from '../types/Book';
import { goalService } from '../services/goalService';
import { Goal } from '../types/Goal';
import { EssayService } from '../services/essayService';
import { Essay } from '../types/Essay';
import { scheduleService, ScheduleEvent } from '../services/scheduleService';
import TaskTimer from './timer/TaskTimer';
import './Days.css';
import { trackAction } from '../services/activityService';

interface DaysProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

// Interface for the notes field structure
interface NotesBlockStructure {
  block_ids: string[];
}

// Enhanced DailyNote interface that only supports block structure
interface EnhancedDailyNote extends Omit<DailyNote, 'notes'> {
  notes: NotesBlockStructure;
  blocks?: ContentBlock[];
}

const Days: React.FC<DaysProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { date } = useParams<{ date?: string }>();

  const validateDate = (dateString: string | undefined): string | null => {
    if (!dateString) return null;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return null;
    
    const [year, month, day] = dateString.split('-').map(Number);
    const d = new Date(year, month - 1, day);

    if (d && d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
      return dateString;
    }
    return null;
  };

  const [currentNote, setCurrentNote] = useState<EnhancedDailyNote | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    return validateDate(date) || new Date().toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCalendarDrawer, setShowCalendarDrawer] = useState(false);
  const [showOptionsDrawer, setShowOptionsDrawer] = useState(false);
  const [showHabitsSection, setShowHabitsSection] = useState(false);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [habitCompletions, setHabitCompletions] = useState<{ [habitId: number]: HabitCompletion | boolean }>({});
  const [habitScores, setHabitScores] = useState<{ [habitId: number]: HabitScore }>({});
  const [habitsLoading, setHabitsLoading] = useState(false);
  const [completionModalState, setCompletionModalState] = useState<{ habit: Habit; date: string } | null>(null);
  const [showOnlyIncompleteHabits, setShowOnlyIncompleteHabits] = useState(false);
  const [showTasksSection, setShowTasksSection] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showScheduleSection, setShowScheduleSection] = useState(false);
  const [scheduleEvents, setScheduleEvents] = useState<ScheduleEvent[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [showAllSections, setShowAllSections] = useState(true);
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [taskTags, setTaskTags] = useState<{ [taskId: number]: string[] }>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(undefined);
  const [datesWithNotes, setDatesWithNotes] = useState<Set<string>>(new Set());
  const [favoriteKnowledge, setFavoriteKnowledge] = useState<KnowledgeCategory[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeLessonCounts, setKnowledgeLessonCounts] = useState<{ [categoryId: string]: number }>({});
  const [onGoingNovels, setOnGoingNovels] = useState<Novel[]>([]);
  const [novelsLoading, setNovelsLoading] = useState(false);
  const [showOnGoingWorksSection, setShowOnGoingWorksSection] = useState(false);
  const [onGoingBooks, setOnGoingBooks] = useState<Book[]>([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [onGoingEssays, setOnGoingEssays] = useState<Essay[]>([]);
  const [essaysLoading, setEssaysLoading] = useState(false);
  const [calendarDate, setCalendarDate] = useState(() => {
    // Parse selectedDate as local date to avoid timezone issues
    const [year, month, day] = selectedDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  });
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [taskToMove, setTaskToMove] = useState<Task | null>(null);
  const [selectedMoveDate, setSelectedMoveDate] = useState('');
  const [highlightedTaskIds, setHighlightedTaskIds] = useState<Set<number>>(new Set());
  const [isFullBlockMode, setIsFullBlockMode] = useState(false);
  const [showGoalsSection, setShowGoalsSection] = useState(false);
  const [currentYearGoals, setCurrentYearGoals] = useState<Goal[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalProgressMap, setGoalProgressMap] = useState<Record<string, number>>({});
  const [loadingProgressForGoals, setLoadingProgressForGoals] = useState<Set<string>>(new Set());
  const [daysSectionConfig, setDaysSectionConfig] = useState<DaysSectionConfig | null>(null);
  const [showYearMonthSelector, setShowYearMonthSelector] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [taskForTimer, setTaskForTimer] = useState<Task | null>(null);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [projectNewTaskForm, setProjectNewTaskForm] = useState<string | null>(null); // projectId for which form is open
  const [projectNewTaskTitle, setProjectNewTaskTitle] = useState<{ [projectId: string]: string }>({});
  const [projectNewTaskDescription, setProjectNewTaskDescription] = useState<{ [projectId: string]: string }>({});
  const [projectNewTaskTags, setProjectNewTaskTags] = useState<{ [projectId: string]: string[] }>({});
  const [creatingProjectTask, setCreatingProjectTask] = useState<string | null>(null); // projectId for which task is being created

  // Get user ID
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Create a ref to hold the current note for the callback
  const currentNoteRef = useRef<EnhancedDailyNote | null>(null);
  
  // Update the ref whenever currentNote changes
  useEffect(() => {
    console.log('🔄 Days::useEffect::currentNoteRef::Updating currentNoteRef:', currentNote, 'user ', user);
    currentNoteRef.current = currentNote;
  }, [currentNote]);

  // Block management with standardized hook
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loading: blocksLoading,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: getUserId(),
    containerType: 'dailyNote',
    containerId: '', // Start with empty, use setContainerId to update dynamically
    onContainerUpdate: async (container: BlockContainer) => {
      // Use the ref to get the current value, not the captured closure value
      const currentNoteValue = currentNoteRef.current;
      
      console.log('🔄 Days::onContainerUpdate called:', {
        containerBlocks: container.blocks,
        currentNoteId: currentNoteValue?.note_id
      });

      if (currentNoteValue) {
        const notesData: NotesBlockStructure = { block_ids: container.blocks };
        
        await DailyNotesService.updateDailyNote(currentNoteValue.note_id, {
          notes: JSON.stringify(notesData)
        });

        trackAction(user, 'NOTE_EDIT', { noteId: currentNoteValue.note_id, date: currentNoteValue.note_date });
      }
    }
  });

  // Helper function to check if parsed JSON is valid block structure
  const isBlockStructure = (parsed: any): parsed is NotesBlockStructure => {
    return parsed && typeof parsed === 'object' && Array.isArray(parsed.block_ids);
  };

  // Load note for selected date (block structure only)
  const loadNoteForDate = async (date: string) => {
    console.log('🔄 Days::loadNoteForDate::Loading note for date:', date, 'user ', user);

    if (!user) return;

    trackAction(user, 'DAY_VIEW', { date });

    setLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const note = await DailyNotesService.getDailyNoteByDate(userId, date);
      
      if (note) {
        // Parse notes field - expect JSON string with block structure
        let notesData: NotesBlockStructure;
        
        try {
          // Handle empty or null notes field
          const notesField = note.notes || '';
          
          // Set container ID first to clear old blocks
          console.log('🔄 Days::loadNoteForDate::Setting container ID:', note.note_id, 'for date:', date);
          setContainerId(note.note_id);
          
          if (notesField.trim() === '') {
            notesData = { block_ids: [] };
          } else {
            const parsed = JSON.parse(notesField);
            if (isBlockStructure(parsed)) {
              notesData = parsed;
              // Load the content blocks using block manager
              console.log('📋 Days::loadNoteForDate::Loading blocks:', parsed.block_ids, 'for date:', date);
              await loadBlocks(parsed.block_ids);
            } else {
              console.warn('⚠️ Days::loadNoteForDate::Invalid block structure format in note:', parsed);
              // Initialize empty block structure for invalid format
              notesData = { block_ids: [] };
            }
          }
        } catch (parseError) {
          console.warn('⚠️ Days::loadNoteForDate::Failed to parse notes field as JSON:', parseError);
          // Initialize empty block structure for invalid JSON
          notesData = { block_ids: [] };
        }
        
        const enhancedNote: EnhancedDailyNote = {
          ...note,
          notes: notesData
        };
        
        setCurrentNote(enhancedNote);
      } else {
        // No note exists for this date - clear container ID and blocks
        console.log('🔄 loadNoteForDate::No note found for date:', date, '- clearing container');
        setContainerId(''); // This will now clear blocks due to our BlockManager update
        setCurrentNote(null);
      }
    } catch (error: any) {
      console.error('loadNoteForDate::Error loading note:', error);
      setError('Failed to load note for this date');
      // Set empty state on error
      setContainerId(''); // Clear container first to reset blocks
      setCurrentNote(null);
    } finally {
      setLoading(false);
    }
  };


  // Save note - now simplified since individual blocks are saved automatically
  const saveNote = async () => {
    if (!user) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // With the new streamlined approach, individual blocks are already saved
      // We just need to ensure the daily note references all current blocks
      if (currentNote && contentBlocks.length > 0) {
        const blockIds = contentBlocks.map(block => block.block_id);
        const notesData: NotesBlockStructure = { block_ids: blockIds };
        
        await DailyNotesService.updateDailyNote(currentNote.note_id, {
          notes: JSON.stringify(notesData)
        });
        
        const enhancedNote: EnhancedDailyNote = {
          ...currentNote,
          notes: notesData,
          blocks: contentBlocks
        };
        setCurrentNote(enhancedNote);
        setSuccessMessage(`Note structure updated! (${blockIds.length} blocks linked)`);
      } else if (!currentNote && contentBlocks.length > 0) {
        // Create new daily note with existing blocks
        const userId = getUserId();
        const blockIds = contentBlocks.map(block => block.block_id);
        const notesData: NotesBlockStructure = { block_ids: blockIds };
        
        const newNote = await DailyNotesService.createDailyNote({
          user_id: userId,
          note_date: selectedDate,
          notes: JSON.stringify(notesData)
        });
        
        const enhancedNote: EnhancedDailyNote = {
          ...newNote,
          notes: notesData,
          blocks: contentBlocks
        };
        setCurrentNote(enhancedNote);
        setSuccessMessage(`Daily note created! (${blockIds.length} blocks linked)`);
      } else {
        setSuccessMessage('No changes to save');
      }


      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving note structure:', error);
      setError('Failed to save note structure. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle date change
  const handleDateChange = (date: string) => {
    // Navigate to the new date URL - let URL sync handle selectedDate update
    navigate(`/days/${date}`);
  };

  // Load dates with notes for calendar highlighting
  const loadDatesWithNotes = async (targetDate?: Date) => {
    if (!user) return;

    try {
      const userId = getUserId();
      const dateToUse = targetDate || calendarDate;
      const currentYear = dateToUse.getFullYear();
      const currentMonth = dateToUse.getMonth();
      
      // Get start and end dates for the current month view (including previous/next month dates)
      const startOfMonth = new Date(currentYear, currentMonth, 1);
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
      
      // Extend to show full calendar grid (6 weeks)
      const startOfCalendar = new Date(startOfMonth);
      startOfCalendar.setDate(startOfCalendar.getDate() - startOfCalendar.getDay());
      
      const endOfCalendar = new Date(endOfMonth);
      endOfCalendar.setDate(endOfCalendar.getDate() + (6 - endOfCalendar.getDay()));
      
      const response = await DailyNotesService.getUserDailyNotes(userId, {
        start_date: startOfCalendar.toISOString().split('T')[0],
        end_date: endOfCalendar.toISOString().split('T')[0],
        limit: 50
      });
      
      // Convert note dates to YYYY-MM-DD format in local timezone for comparison with calendar dates
      const noteDates = new Set(
        response.data
          .filter(note => note.notes && note.notes.trim() !== '' && note.notes !== '{}')
          .map(note => {
            // Parse ISO date string and convert to local date to match calendar date format
            // This handles timezone differences (e.g., "2008-12-03T17:00:00.000Z" UTC = "2008-12-04" in UTC+7)
            const date = new Date(note.note_date);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })
      );
      setDatesWithNotes(noteDates);
    } catch (error: any) {
      console.error('Error loading dates with notes:', error);
    }
  };

  // Load habits for selected date
  const loadHabitsForDate = async (date: string) => {
    if (!user) return;

    setHabitsLoading(true);
    try {
      const userId = getUserId();
      
      // Load user habits and date's completions in parallel
      const [habitsResponse, completionsResponse] = await Promise.all([
        habitService.getUserHabits(userId, { is_active: true }),
        habitService.getDailyCompletions(userId, date)
      ]);
      
      if (habitsResponse.success && habitsResponse.habits) {
        setHabits(habitsResponse.habits);
        
        // Process completions
        const completions: { [habitId: number]: HabitCompletion | boolean } = {};
        if (completionsResponse.success && completionsResponse.completions) {
          completionsResponse.completions.forEach((completion) => {
            completions[completion.habit_id] = {
              ...completion,
              is_completed: Boolean(completion.is_completed)
            };
          });
        }
        
        // Initialize uncompleted habits
        habitsResponse.habits.forEach((habit) => {
          if (!completions[habit.id]) {
            completions[habit.id] = false;
          }
        });
        
        setHabitCompletions(completions);
        
        // Load habit scores
        await loadHabitScores(habitsResponse.habits, userId);
      }
    } catch (error) {
      console.error('Error loading habits:', error);
    } finally {
      setHabitsLoading(false);
    }
  };

  // Load habit scores
  const loadHabitScores = async (habits: Habit[], userId: number) => {
    try {
      // Fetch scores for all habits in parallel
      const scorePromises = habits.map(async (habit) => {
        try {
          const scoreResponse = await habitService.getHabitScore(habit.id, userId, { days: 30 });
          if (scoreResponse.success && scoreResponse.score) {
            return { habitId: habit.id, score: scoreResponse.score };
          }
          return { habitId: habit.id, score: null };
        } catch (error) {
          console.error(`Error fetching score for habit ${habit.id}:`, error);
          return { habitId: habit.id, score: null };
        }
      });

      const scoreResults = await Promise.all(scorePromises);
      
      // Update scores state
      const scoresMap: { [habitId: number]: HabitScore } = {};
      scoreResults.forEach(result => {
        if (result.score) {
          scoresMap[result.habitId] = result.score;
        }
      });
      
      setHabitScores(scoresMap);
    } catch (error) {
      console.error('Error loading habit scores:', error);
    }
  };

  const loadFavoriteKnowledge = async () => {
    if (!user) return;

    setKnowledgeLoading(true);
    try {
        const result = await KnowledgeCategoryService.getCategories();
        if (result.categories) {
            const favorites = result.categories.filter(cat => cat.is_favorite);
            setFavoriteKnowledge(favorites);
            
            // Fetch lesson counts for each favorite category
            const lessonCountPromises = favorites.map(async (category) => {
              try {
                const lessonsResult = await KnowledgeCategoryService.getPastLessons(
                  category.category_slug,
                  1, // Just need the total count, so limit to 1
                  0
                );
                return {
                  categoryId: category.category_id,
                  count: lessonsResult.total || 0
                };
              } catch (err) {
                console.error(`Error loading lesson count for category ${category.category_name}:`, err);
                return {
                  categoryId: category.category_id,
                  count: 0
                };
              }
            });
            
            const lessonCounts = await Promise.all(lessonCountPromises);
            const countsMap: { [categoryId: string]: number } = {};
            lessonCounts.forEach(({ categoryId, count }) => {
              countsMap[categoryId] = count;
            });
            setKnowledgeLessonCounts(countsMap);
        }
    } catch (error) {
        console.error('Error loading favorite knowledge categories:', error);
    } finally {
        setKnowledgeLoading(false);
    }
  };

  // Load on-going novels
  const loadOnGoingNovels = async () => {
    if (!user) return;

    setNovelsLoading(true);
    try {
      const userId = getUserId();
      const novels = await NovelService.getUserNovels(userId);
      // Filter for on-going status
      const onGoing = novels.filter(novel => novel.status === 'on-going');
      setOnGoingNovels(onGoing);
    } catch (error) {
      console.error('Error loading on-going novels:', error);
    } finally {
      setNovelsLoading(false);
    }
  };

  // Load on-going books
  const loadOnGoingBooks = async () => {
    if (!user) return;

    setBooksLoading(true);
    try {
      const result = await bookService.getBooksWithProgress(50, 0);
      if (result && result.books) {
        // Filter for books with readingStatus === 'reading'
        const onGoing = result.books.filter((book: Book) => book.readingStatus === 'reading');
        setOnGoingBooks(onGoing);
      }
    } catch (error) {
      console.error('Error loading on-going books:', error);
    } finally {
      setBooksLoading(false);
    }
  };

  // Load on-going essays
  const loadOnGoingEssays = async () => {
    if (!user) return;

    setEssaysLoading(true);
    try {
      const userId = getUserId();
      const essays = await EssayService.getOnGoingEssays(userId);
      setOnGoingEssays(essays);
    } catch (error) {
      console.error('Error loading on-going essays:', error);
    } finally {
      setEssaysLoading(false);
    }
  };

  // Get active goals where selectedDate falls within the goal's date range
  const getActiveGoalsForCurrentYear = (goals: Goal[]): Goal[] => {
    if (!selectedDate) return [];

    return goals.filter(goal => {
      // Goals must have at least one date to be included
      if (!goal.startedDate && !goal.targetDate) {
        return false;
      }

      // Compare dates as strings (YYYY-MM-DD format) to avoid timezone issues
      if (goal.startedDate && goal.targetDate) {
        // Goal has both start and end dates - selectedDate must be within range
        return goal.startedDate <= selectedDate && selectedDate <= goal.targetDate;
      } else if (goal.startedDate) {
        // Goal has only start date - active if started before or on selectedDate
        return goal.startedDate <= selectedDate;
      } else if (goal.targetDate) {
        // Goal has only end date - active if ending on or after selectedDate
        return selectedDate <= goal.targetDate;
      }

      return false;
    });
  };

  // Load goals for the current year
  const loadCurrentYearGoals = async () => {
    if (!user) return;

    setGoalsLoading(true);
    try {
      const userId = getUserId();
      const response = await goalService.getUserGoals({
        user_id: userId.toString()
      });

      if (response.success && response.goals) {
        const convertedGoals = response.goals.map((goal: any) => goalService.convertFirebaseGoal(goal));
        const activeGoals = getActiveGoalsForCurrentYear(convertedGoals);
        setCurrentYearGoals(activeGoals);
        
        // Load progress for all active goals
        await loadProgressForGoals(activeGoals);
      }
    } catch (error) {
      console.error('Error loading current year goals:', error);
    } finally {
      setGoalsLoading(false);
    }
  };

  // Load progress for goals
  const loadProgressForGoals = async (goals: Goal[]) => {
    if (goals.length === 0) return;

    const goalsToLoad = goals.filter(goal => !goalProgressMap[goal.id] && !loadingProgressForGoals.has(goal.id));
    
    if (goalsToLoad.length === 0) return;

    // Mark goals as loading
    setLoadingProgressForGoals(prev => {
      const next = new Set(prev);
      goalsToLoad.forEach(goal => next.add(goal.id));
      return next;
    });

    try {
      const progressMap: Record<string, number> = {};

      await Promise.all(
        goalsToLoad.map(async (goal) => {
          try {
            const response = await goalService.calculateGoalProgress(goal.id);
            if (response.success && response.progress) {
              // Use calculated progress if available, otherwise fall back to manual progress
              progressMap[goal.id] = response.progress.overall || goal.progress || 0;
            } else {
              progressMap[goal.id] = goal.progress || 0;
            }
          } catch (error) {
            console.warn(`Failed to calculate progress for goal ${goal.id}:`, error);
            // Fall back to manual progress
            progressMap[goal.id] = goal.progress || 0;
          }
        })
      );

      // Update progress map with new values
      setGoalProgressMap(prev => ({ ...prev, ...progressMap }));
    } catch (error) {
      console.error('Error loading progress for goals:', error);
    } finally {
      // Remove goals from loading set
      setLoadingProgressForGoals(prev => {
        const next = new Set(prev);
        goalsToLoad.forEach(goal => next.delete(goal.id));
        return next;
      });
    }
  };

  // Handle goal click to navigate to goal detail
  const handleGoalClick = (goalId: string) => {
    navigate(`/goals/${goalId}`);
  };

  // Calculate progress color based on percentage (same as Lifetime.tsx)
  const getProgressColor = (progress: number): string => {
    if (progress === 0) return '#e0e0e0'; // gray for not started
    if (progress < 30) return '#dc3545'; // red for low progress
    if (progress < 60) return '#ffc107'; // yellow for medium progress
    if (progress < 90) return '#17a2b8'; // teal for good progress
    return '#28a745'; // green for excellent progress
  };


  // Helper function to check if a habit has met its quota for its frequency period
  const hasMetQuota = (habit: Habit): boolean => {
    const completion = habitCompletions[habit.id];
    const score = habitScores[habit.id];
    
    // For daily habits, check today's completion
    if (habit.frequency_type === 'daily') {
      if (!completion) return false;
      
      if (typeof completion === 'object') {
        return Boolean(completion.target_achieved);
      }
      return Boolean(completion);
    }
    
    // For weekly/monthly habits, use the habit score
    // Score represents the completion rate for the period
    // If score >= 1.0 (100%), the quota is met for the period
    if (habit.frequency_type === 'weekly' || habit.frequency_type === 'monthly') {
      if (!score) return false;
      
      // Score is between 0-1, so 1.0 means 100% (quota met)
      return score.score >= 1.0;
    }
    
    // Fallback: check if there's a completion for today
    if (!completion) return false;
    
    if (typeof completion === 'object') {
      return Boolean(completion.target_achieved);
    }
    
    return Boolean(completion);
  };

  // Filter habits based on showOnlyIncompleteHabits toggle
  const getFilteredHabits = (): Habit[] => {
    if (!showOnlyIncompleteHabits) {
      return habits;
    }
    
    // Show only habits that haven't met their quota
    return habits.filter(habit => !hasMetQuota(habit));
  };

  // Toggle habit completion
  const toggleHabitCompletion = async (habitId: number) => {
    if (!user) return;

    const habit = habits.find(h => h.id === habitId);
    if (!habit) {
      console.error(`Habit with id ${habitId} not found`);
      return;
    }

    if (habit.tracking_type === 'boolean') {
      try {
        const userId = getUserId();
        const currentCompletion = habitCompletions[habitId];
        const isCurrentlyCompleted = typeof currentCompletion === 'object' 
          ? Boolean(currentCompletion.is_completed)
          : Boolean(currentCompletion);
        
        const completionData: RecordCompletionData = {
          habit_id: habitId,
          user_id: userId,
          completion_date: selectedDate,
          is_completed: !isCurrentlyCompleted
        };
        
        const response = await habitService.recordCompletion(completionData);
        
        if (response.success) {
          trackAction(user, 'HABIT_COMPLETION', { habitId, completed: completionData.is_completed, date: selectedDate });
          // Update local state
          setHabitCompletions(prev => ({
            ...prev,
            [habitId]: {
              habit_id: habitId,
              completion_date: selectedDate,
              is_completed: !isCurrentlyCompleted,
              target_achieved: response.target_achieved || false,
              achievement_percentage: response.achievement_percentage || 0
            } as HabitCompletion
          }));
        }
      } catch (error) {
        console.error('Error toggling habit completion:', error);
      }
    } else {
      // For count/measurement habits, open the modal
      setCompletionModalState({ habit, date: selectedDate });
    }
  };

  const handleSaveCompletionValue = async (count: number | undefined, measurement: number | undefined) => {
    if (!user || !completionModalState) return;

    try {
      const userId = user.id;
      const { habit, date } = completionModalState;

      const completionData: RecordCompletionData = {
        habit_id: habit.id,
        user_id: userId,
        completion_date: date,
        is_completed: count !== undefined || measurement !== undefined,
        completion_count: count,
        measurement_value: measurement,
      };

      const response = await habitService.recordCompletion(completionData);

      if (response.success) {
        trackAction(user, 'HABIT_COMPLETION', { 
            habitId: habit.id, 
            completed: completionData.is_completed, 
            date: date,
            count,
            measurement,
        });
        // Update local state
        setHabitCompletions(prev => ({
          ...prev,
          [habit.id]: {
            habit_id: habit.id,
            completion_date: date,
            is_completed: count !== undefined || measurement !== undefined,
            completion_count: count,
            measurement_value: measurement,
            target_achieved: response.target_achieved || false,
            achievement_percentage: response.achievement_percentage || 0,
          } as HabitCompletion,
        }));
        setCompletionModalState(null);
      } else {
        setError(response.message || 'Failed to update habit completion');
      }
    } catch (err) {
      console.error('Error saving completion value:', err);
      setError('Failed to save completion value. Please try again.');
    }
  };


  // Load tasks for selected date
  const loadTasksForDate = async (date: string) => {
    if (!user) return;

    setTasksLoading(true);
    try {
      const userId = user.id.toString();
      const today = new Date().toISOString().split('T')[0];
      const isToday = date === today;
      
      // Fetch tasks assigned to the selected date
      const response = await taskService.getTasksAssignedForDate(userId, date);
      
      let allTasks: Task[] = [];
      
      if (response.success && response.tasks) {
        allTasks = [...response.tasks];
      }
      
      // If viewing today, also fetch overdue tasks
      if (isToday) {
        try {
          const overdueResponse = await taskService.getOverdueTasks(userId);
          if (overdueResponse.success && overdueResponse.tasks) {
            // Merge overdue tasks, avoiding duplicates
            const existingTaskIds = new Set(allTasks.map(t => t.id));
            const overdueTasks = overdueResponse.tasks.filter(t => !existingTaskIds.has(t.id));
            allTasks = [...allTasks, ...overdueTasks];
          }
        } catch (error) {
          console.error('Error loading overdue tasks:', error);
          // Continue with assigned tasks even if overdue fetch fails
        }
      }
      
      if (allTasks.length > 0) {
        // Sort tasks: highlighted first, then by completion status (completed at bottom)
        const sortedTasks = [...allTasks].sort((a, b) => {
          const aHighlighted = highlightedTaskIds.has(a.id);
          const bHighlighted = highlightedTaskIds.has(b.id);
          
          // First sort by highlight status (highlighted first)
          if (aHighlighted !== bHighlighted) {
            return aHighlighted ? -1 : 1;
          }
          
          // Then sort by completion status (completed at bottom)
          const aCompleted = a.status === 'completed';
          const bCompleted = b.status === 'completed';
          if (aCompleted === bCompleted) return 0;
          return aCompleted ? 1 : -1;
        });
        setTasks(sortedTasks);
        
        // Use tags from API response (now included via JOIN query)
        const tagsMap: { [taskId: number]: string[] } = {};
        sortedTasks.forEach(task => {
          if (task.tags && task.tags.length > 0) {
            tagsMap[task.id] = task.tags;
          }
        });
        setTaskTags(tagsMap);
      } else {
        setTasks([]);
        setTaskTags({});
      }
    } catch (error) {
      console.error('Error loading tasks for date:', error);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  // Load schedule events for selected date
  const loadScheduleForDate = async (date: string) => {
    if (!user) return;

    setScheduleLoading(true);
    try {
      const userId = getUserId();
      const events = await scheduleService.getScheduleEventsByDate(userId, date);
      // Sort events by time
      const sortedEvents = events.sort((a, b) => a.event_time.localeCompare(b.event_time));
      setScheduleEvents(sortedEvents);
    } catch (error) {
      console.error('Error loading schedule events:', error);
      setScheduleEvents([]);
    } finally {
      setScheduleLoading(false);
    }
  };

  // Load available tags
  const loadAvailableTags = async () => {
    try {
      const response = await tagService.getAllTags();
      if (response.success && response.tags) {
        setAvailableTags(response.tags);
      }
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  };


  // Toggle task completion (wrapper for TaskRow component)
  const toggleTaskCompletion = async (task: Task) => {
    if (!user) return;

    try {
      const newStatus: 'todo' | 'completed' = task.status === 'completed' ? 'todo' : 'completed';
      const response = await taskService.updateTask({
        id: task.id.toString(),
        status: newStatus
      });

      if (response.success) {
        trackAction(user, 'TASK_COMPLETE', { taskId: task.id, status: newStatus, date: selectedDate });
        // Update local state and sort: completed tasks at the bottom
        setTasks(prevTasks => {
          const updated = prevTasks.map(t =>
            t.id === task.id
              ? { ...t, status: newStatus as Task['status'] }
              : t
          );
          // Sort: completed tasks at the bottom
          return updated.sort((a, b) => {
            const aCompleted = a.status === 'completed';
            const bCompleted = b.status === 'completed';
            if (aCompleted === bCompleted) return 0;
            return aCompleted ? 1 : -1;
          });
        });
      }
    } catch (error) {
      console.error('Error toggling task completion:', error);
    }
  };

  // Handle task click to navigate to task detail
  const handleTaskClick = (taskId: number) => {
    navigate(`/tasks/${taskId}`);
  };

  // Toggle task highlight
  const toggleTaskHighlight = (task: Task) => {
    setHighlightedTaskIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(task.id)) {
        newSet.delete(task.id);
      } else {
        newSet.add(task.id);
      }
      return newSet;
    });
    // Sorting will be handled by the useEffect that watches highlightedTaskIds
  };

  // Create a new task
  const createNewTask = async () => {
    if (!user || !newTaskTitle.trim()) return;

    setCreatingTask(true);
    try {
      const taskData = {
        title: newTaskTitle.trim(),
        description: newTaskDescription.trim() || undefined,
        status: 'todo' as const,
        assigned_date: selectedDate,
        project_id: selectedProjectId,
        tag_names: selectedTags.length > 0 ? selectedTags : undefined
      };

      const response = await taskService.createTask(user.id.toString(), taskData);
      
      if (response.success) {
        trackAction(user, 'TASK_CREATE', { ...taskData, taskId: response.id });
        // Clear form
        setNewTaskTitle('');
        setNewTaskDescription('');
        setSelectedTags([]);
        setSelectedProjectId(undefined);
        setShowNewTaskForm(false);
        
        // Reload tasks for the current date
        await loadTasksForDate(selectedDate);
        
        setSuccessMessage('Task created successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to create task. Please try again.');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Failed to create task. Please try again.');
    } finally {
      setCreatingTask(false);
    }
  };

  // Cancel task creation
  const cancelTaskCreation = () => {
    setNewTaskTitle('');
    setNewTaskDescription('');
    setSelectedTags([]);
    setSelectedProjectId(undefined);
    setShowNewTaskForm(false);
  };

  // Create a new task for a specific project
  const createProjectTask = async (projectId: string) => {
    if (!user || !projectNewTaskTitle[projectId]?.trim()) return;

    setCreatingProjectTask(projectId);
    try {
      const taskData = {
        title: projectNewTaskTitle[projectId].trim(),
        description: projectNewTaskDescription[projectId]?.trim() || undefined,
        status: 'todo' as const,
        assigned_date: selectedDate,
        project_id: Number(projectId),
        tag_names: projectNewTaskTags[projectId]?.length > 0 ? projectNewTaskTags[projectId] : undefined
      };

      const response = await taskService.createTask(user.id.toString(), taskData);
      
      if (response.success) {
        trackAction(user, 'TASK_CREATE', { ...taskData, taskId: response.id });
        // Clear form for this project
        setProjectNewTaskTitle(prev => {
          const next = { ...prev };
          delete next[projectId];
          return next;
        });
        setProjectNewTaskDescription(prev => {
          const next = { ...prev };
          delete next[projectId];
          return next;
        });
        setProjectNewTaskTags(prev => {
          const next = { ...prev };
          delete next[projectId];
          return next;
        });
        setProjectNewTaskForm(null);
        
        // Reload tasks for the current date
        await loadTasksForDate(selectedDate);
        
        setSuccessMessage('Task created successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to create task. Please try again.');
      }
    } catch (error) {
      console.error('Error creating project task:', error);
      setError('Failed to create task. Please try again.');
    } finally {
      setCreatingProjectTask(null);
    }
  };

  // Cancel project task creation
  const cancelProjectTaskCreation = (projectId: string) => {
    setProjectNewTaskTitle(prev => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
    setProjectNewTaskDescription(prev => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
    setProjectNewTaskTags(prev => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
    setProjectNewTaskForm(null);
  };

  // Load projects
  const loadProjects = async () => {
    if (!user?.id) return;
    try {
      const response = await projectService.getUserProjects({
        user_id: user.id.toString()
      });
      if (response.success && response.projects) {
        const convertedProjects = response.projects.map(project => projectService.convertDatabaseProject(project));
        setProjects(convertedProjects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  // Load active projects (status === 'in_progress')
  const loadActiveProjects = async () => {
    if (!user?.id) return;
    try {
      const response = await projectService.getUserProjects({
        user_id: user.id.toString()
      });
      if (response.success && response.projects) {
        const convertedProjects = response.projects.map(project => projectService.convertDatabaseProject(project));
        // Filter for in_progress projects
        const active = convertedProjects.filter(project => project.status === ProjectStatus.IN_PROGRESS);
        setActiveProjects(active);
      }
    } catch (error) {
      console.error('Error loading active projects:', error);
    }
  };

  // Calculate tomorrow's date (using local date to avoid timezone issues)
  const calculateTomorrow = (): string => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const tomorrow = new Date(year, month - 1, day);
    tomorrow.setDate(tomorrow.getDate() + 1);
    // Use local date formatting to avoid timezone issues
    const tomorrowYear = tomorrow.getFullYear();
    const tomorrowMonth = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const tomorrowDay = String(tomorrow.getDate()).padStart(2, '0');
    return `${tomorrowYear}-${tomorrowMonth}-${tomorrowDay}`;
  };

  // Calculate next week (same day, next week) - using local date to avoid timezone issues
  const calculateNextWeek = (currentDate: string): string => {
    const [year, month, day] = currentDate.split('-').map(Number);
    const nextWeek = new Date(year, month - 1, day);
    nextWeek.setDate(nextWeek.getDate() + 7);
    // Use local date formatting to avoid timezone issues
    const nextWeekYear = nextWeek.getFullYear();
    const nextWeekMonth = String(nextWeek.getMonth() + 1).padStart(2, '0');
    const nextWeekDay = String(nextWeek.getDate()).padStart(2, '0');
    return `${nextWeekYear}-${nextWeekMonth}-${nextWeekDay}`;
  };

  // Calculate Saturday of the same week - using local date to avoid timezone issues
  const calculateSaturdayOfSameWeek = (currentDate: string): string => {
    const [year, month, day] = currentDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    // Calculate days to add to get to Saturday (6)
    // If it's already Saturday (dayOfWeek === 6), return the same date
    // Otherwise, add (6 - dayOfWeek) days
    const daysToAdd = dayOfWeek === 6 ? 0 : 6 - dayOfWeek;
    date.setDate(date.getDate() + daysToAdd);
    // Use local date formatting to avoid timezone issues
    const saturdayYear = date.getFullYear();
    const saturdayMonth = String(date.getMonth() + 1).padStart(2, '0');
    const saturdayDay = String(date.getDate()).padStart(2, '0');
    return `${saturdayYear}-${saturdayMonth}-${saturdayDay}`;
  };

  // Handle task menu actions (wrapper for TaskRow component)
  const handleTaskMenuAction = async (task: Task, action: 'today' | 'tomorrow' | 'next-week' | 'backlog' | 'custom-date' | 'delete' | 'log-time') => {
    const taskId = task.id;
    
    if (!user) return;

    try {
      if (action === 'delete') {
        if (!window.confirm('Delete this task?')) return;
        
        const response = await taskService.deleteTask(taskId.toString());
        
        if (response.success) {
          // Remove task from local state
          setTasks(prev => prev.filter(t => t.id !== taskId));
          setSuccessMessage('Task deleted successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          setError('Failed to delete task. Please try again.');
        }
      } else {
        let newDate: string | null = null;
        
        if (action === 'today') {
          newDate = today;
          console.log('📅 Task menu: Assigning to today. Today is:', today);
        } else if (action === 'tomorrow') {
          newDate = calculateTomorrow();
          console.log('📅 Task menu: Rescheduling to tomorrow. Current date:', selectedDate, 'Tomorrow:', newDate);
        } else if (action === 'next-week') {
          newDate = calculateNextWeek(selectedDate);
          console.log('📅 Task menu: Rescheduling to next week. Current date:', selectedDate, 'Next week:', newDate);
        } else if (action === 'backlog') {
          newDate = null; // Remove assigned_date
        } else if (action === 'custom-date') {
          // Open date picker modal
          setTaskToMove(task);
          setSelectedMoveDate(selectedDate); // Default to current selected date
          setShowDatePickerModal(true);
          return; // Exit early, will handle update in modal
        } else if (action === 'log-time') {
          // Open timer modal
          setTaskForTimer(task);
          setShowTimerModal(true);
          return; // Exit early, timer modal handles its own logic
        }
        
        const updateData: any = {
          id: taskId.toString()
        };
        
        if (action === 'backlog') {
          // Explicitly set to null to remove assigned_date
          updateData.assigned_date = null;
        } else if (newDate) {
          updateData.assigned_date = newDate;
        }
        
        console.log('📅 Task menu: Updating task with data:', updateData);
        const response = await taskService.updateTask(updateData);
        console.log('📅 Task menu: Update response:', response);
        
        if (response.success) {
          // Reload tasks for current date (task will be removed if assigned_date changed)
          await loadTasksForDate(selectedDate);
          
          const actionMessage = action === 'backlog' 
            ? 'Task moved to backlog' 
            : action === 'today'
            ? `Task assigned to today (${newDate})`
            : action === 'tomorrow'
            ? `Task rescheduled to tomorrow (${newDate})`
            : `Task rescheduled to next week (${newDate})`;
          
          setSuccessMessage(actionMessage);
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          console.error('📅 Task menu: Update failed. Response:', response);
          setError(`Failed to update task: ${response.message || 'Please try again.'}`);
        }
      }
    } catch (error) {
      console.error('📅 Task menu: Error handling task menu action:', error);
      setError(`Failed to update task: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Handle moving task to custom date
  const handleMoveTaskToDate = async () => {
    if (!user || !taskToMove || !selectedMoveDate) return;

    try {
      const updateData: any = {
        id: taskToMove.id.toString(),
        assigned_date: selectedMoveDate
      };

      const response = await taskService.updateTask(updateData);

      if (response.success) {
        // Reload tasks for current date (task will be removed if assigned_date changed)
        await loadTasksForDate(selectedDate);
        
        setSuccessMessage(`Task moved to ${formatDate(selectedMoveDate)}`);
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Close modal and reset state
        setShowDatePickerModal(false);
        setTaskToMove(null);
        setSelectedMoveDate('');
      } else {
        setError(`Failed to move task: ${response.message || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error moving task to date:', error);
      setError(`Failed to move task: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Cancel date picker modal
  const cancelDatePicker = () => {
    setShowDatePickerModal(false);
    setTaskToMove(null);
    setSelectedMoveDate('');
  };

  // Handle timer modal close
  const handleTimerClose = () => {
    setShowTimerModal(false);
    setTaskForTimer(null);
  };

  // Handle time log created callback
  const handleTimeLogCreated = () => {
    setSuccessMessage('Time logged successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
    handleTimerClose();
  };

  // Handle moving undone tasks from the last 7 days to today
  const handleMoveUndoneTasksFrom7DaysAgo = async () => {
    setShowOptionsDrawer(false); // Close dropdown
    
    if (!user) return;

    try {
      const userId = user.id.toString();
      
      // Calculate start date (7 days ago from selectedDate)
      const [year, month, day] = selectedDate.split('-').map(Number);
      const selectedDateObj = new Date(year, month - 1, day);
      const startDate = new Date(selectedDateObj);
      startDate.setDate(startDate.getDate() - 7);
      
      // Calculate end date (1 day before selectedDate to avoid moving tasks already assigned to selectedDate)
      const endDate = new Date(selectedDateObj);
      endDate.setDate(endDate.getDate() - 1);
      
      // Helper function to format date as YYYY-MM-DD
      const formatDate = (date: Date): string => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      
      console.log('📅 Moving undone tasks: Checking dates from', formatDate(startDate), 'to', formatDate(endDate));
      
      // Collect all undone tasks from all dates in the range
      const allUndoneTasks: Task[] = [];
      
      // Loop through each day from startDate to endDate (inclusive)
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = formatDate(currentDate);
        
        try {
          const response = await taskService.getTasksAssignedForDate(userId, dateStr);
          
          if (response.success && response.tasks) {
            // Filter undone tasks (status !== 'completed')
            const undoneTasks = response.tasks.filter(task => 
              task.status !== 'completed'
            );
            allUndoneTasks.push(...undoneTasks);
          }
        } catch (error) {
          console.error(`Error loading tasks for ${dateStr}:`, error);
          // Continue with other dates even if one fails
        }
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      if (allUndoneTasks.length === 0) {
        setSuccessMessage('No undone tasks found within the last 7 days');
        setTimeout(() => setSuccessMessage(null), 3000);
        return;
      }
      
      // Update each undone task's assigned_date to selectedDate
      let successCount = 0;
      let errorCount = 0;
      
      for (const task of allUndoneTasks) {
        try {
          const updateResponse = await taskService.updateTask({
            id: task.id.toString(),
            assigned_date: selectedDate
          });
          
          if (updateResponse.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Error updating task ${task.id}:`, error);
          errorCount++;
        }
      }
      
      // Reload today's tasks
      await loadTasksForDate(selectedDate);
      
      if (errorCount === 0) {
        setSuccessMessage(`Successfully moved ${successCount} task${successCount !== 1 ? 's' : ''} from the last 7 days to today`);
      } else {
        setSuccessMessage(`Moved ${successCount} task${successCount !== 1 ? 's' : ''}, ${errorCount} failed`);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error moving undone tasks:', error);
      setError(`Failed to move tasks: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Sync URL parameter with selectedDate - simplified to prevent race conditions
  useEffect(() => {
    const validatedUrlDate = validateDate(date);
    if (validatedUrlDate) {
      if (validatedUrlDate !== selectedDate) {
        setSelectedDate(validatedUrlDate);
      }
    } else {
      // URL has invalid or missing date, redirect to a valid one
      navigate(`/days/${selectedDate}`, { replace: true });
    }
  }, [date, navigate, selectedDate]);

  // Track last loaded combination to prevent duplicate loads
  const lastLoadedRef = useRef<string>('');
  
  // Load available tags on mount
  useEffect(() => {
    if (user) {
      loadAvailableTags();
      loadProjects();
      loadActiveProjects();
    }
  }, [user]);

  // Re-sort tasks whenever highlightedTaskIds changes
  useEffect(() => {
    setTasks(prevTasks => {
      if (prevTasks.length === 0) return prevTasks;
      
      const sorted = [...prevTasks].sort((a, b) => {
        const aHighlighted = highlightedTaskIds.has(a.id);
        const bHighlighted = highlightedTaskIds.has(b.id);
        
        // First sort by highlight status (highlighted first)
        if (aHighlighted !== bHighlighted) {
          return aHighlighted ? -1 : 1;
        }
        
        // Then sort by completion status (completed at bottom)
        const aCompleted = a.status === 'completed';
        const bCompleted = b.status === 'completed';
        if (aCompleted === bCompleted) return 0;
        return aCompleted ? 1 : -1;
      });
      
      return sorted;
    });
  }, [highlightedTaskIds]);

  // Clear highlights when date changes (session-only, per-date)
  useEffect(() => {
    setHighlightedTaskIds(new Set());
    // Also collapse all project sections when date changes
    setExpandedProjects(new Set());
  }, [selectedDate]);

  // Load days section configs on mount
  useEffect(() => {
    if (user) {
      loadDaysSectionConfigs();
    }
  }, [user?.id]);

  // Load days section configs
  const loadDaysSectionConfigs = async () => {
    if (!user) return;

    try {
      const config = await getDaysSectionConfigs();
      setDaysSectionConfig(config);
      
      // All sections start collapsed by default
      // Config only controls visibility, not expanded/collapsed state
      setShowHabitsSection(false);
      setShowTasksSection(false);
      setShowScheduleSection(false);
      setShowGoalsSection(false);
      setShowOnGoingWorksSection(false);
    } catch (error) {
      console.error('Error loading days section configs:', error);
      // All sections start collapsed by default
      setShowHabitsSection(false);
      setShowTasksSection(false);
      setShowScheduleSection(false);
      setShowGoalsSection(false);
      setShowOnGoingWorksSection(false);
    }
  };

  // Load data when user or selectedDate changes
  useEffect(() => {
    console.log('🔄 Days::Loading data for date:', selectedDate, 'user:', user?.id);

    if (user && selectedDate) {
      // Create a unique key for this user/date combination
      const loadKey = `${user.id}-${selectedDate}`;
      
      // Prevent duplicate loads for the same user/date combination
      if (lastLoadedRef.current === loadKey) {
        console.log('🔄 Days::Skipping duplicate load for:', loadKey);
        return;
      }
      
      console.log('🔄 Days::Loading data for new combination:', loadKey);
      lastLoadedRef.current = loadKey;
      
      // Clear goal progress map when date changes
      setGoalProgressMap({});
      
      // Load all data for the selected date
      loadNoteForDate(selectedDate);
      loadHabitsForDate(selectedDate);
      loadScheduleForDate(selectedDate);
      loadTasksForDate(selectedDate);
      loadFavoriteKnowledge();
      loadOnGoingNovels();
      loadOnGoingBooks();
      loadOnGoingEssays();
      loadCurrentYearGoals();
      loadActiveProjects();
    }
  }, [user?.id, selectedDate]);

  // Update calendar date when selected date changes
  useEffect(() => {
    // Parse selectedDate as local date to avoid timezone issues
    const [year, month, day] = selectedDate.split('-').map(Number);
    const newCalendarDate = new Date(year, month - 1, day);

    setCalendarDate(currentCalDate => {
      // Check if new date is valid and if month/year has changed from current calendar view
      if (!isNaN(newCalendarDate.getTime()) &&
          (newCalendarDate.getMonth() !== currentCalDate.getMonth() ||
           newCalendarDate.getFullYear() !== currentCalDate.getFullYear())) {
        return newCalendarDate;
      }
      return currentCalDate;
    });
  }, [selectedDate]);

  // Load dates with notes when calendar date changes
  useEffect(() => {
    if (user && calendarDate) {
      loadDatesWithNotes(calendarDate);
    }
  }, [calendarDate, user]);

  // Load dates with notes when calendar drawer opens
  useEffect(() => {
    if (user && showCalendarDrawer && calendarDate) {
      loadDatesWithNotes(calendarDate);
    }
  }, [showCalendarDrawer, user, calendarDate]);


  // Handle clicks outside options dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside options dropdown
      const dropdowns = document.querySelectorAll('.options-menu-dropdown');
      let clickedInsideDropdown = false;
      
      dropdowns.forEach((dropdown) => {
        if (dropdown.contains(target)) {
          clickedInsideDropdown = true;
        }
      });
      
      // Check if click is on the options button
      const optionsButtons = document.querySelectorAll('.page-header-icon-btn');
      let clickedOnOptionsButton = false;
      
      optionsButtons.forEach((button) => {
        if (button.contains(target)) {
          clickedOnOptionsButton = true;
        }
      });
      
      if (!clickedInsideDropdown && !clickedOnOptionsButton) {
        setShowOptionsDrawer(false);
      }
    };

    if (showOptionsDrawer) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsDrawer]);

  // Handle clicks outside year/month selector to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const selector = document.querySelector('.year-month-selector');
      const button = document.querySelector('.calendar-month-year-btn');
      
      if (selector && button && showYearMonthSelector) {
        if (!selector.contains(target) && !button.contains(target)) {
          setShowYearMonthSelector(false);
        }
      }
    };

    if (showYearMonthSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showYearMonthSelector]);

  // Format date for display
  const formatDate = (dateString: string): string => {
    // Parse the date string as local date to avoid timezone issues
    // dateString format: "YYYY-MM-DD"
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleKnowledgeClick = (categoryId: string) => {
    navigate(`/knowledge/${categoryId}`);
  };

  const handleNovelClick = (novelId: string) => {
    navigate(`/novel/${novelId}`);
  };

  const handleBookClick = (bookId: string) => {
    navigate(`/books/${bookId}`);
  };

  // Get today's date
  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;

  // Toggle project section expansion
  const toggleProjectExpansion = (projectId: string) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Filter tasks into project tasks and general tasks
  const projectTaskMap: { [projectId: string]: Task[] } = {};
  const generalTasks: Task[] = [];
  
  // Create a map of active project IDs for quick lookup
  const activeProjectIds = new Set(activeProjects.map(p => p.id));
  
  tasks.forEach(task => {
    if (task.project_id && activeProjectIds.has(task.project_id.toString())) {
      const projectId = task.project_id.toString();
      if (!projectTaskMap[projectId]) {
        projectTaskMap[projectId] = [];
      }
      projectTaskMap[projectId].push(task);
    } else {
      generalTasks.push(task);
    }
  });

  // Calendar helper functions
  const generateCalendarDays = (year: number, month: number) => {
    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);
    const startOfCalendar = new Date(startOfMonth);
    startOfCalendar.setDate(startOfCalendar.getDate() - startOfMonth.getDay());
    
    const days = [];
    const currentDate = new Date(startOfCalendar);
    
    // Generate 6 weeks (42 days) for complete calendar grid
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  const formatCalendarDate = (date: Date): string => {
    // Use local date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isCurrentMonth = (date: Date, month: number): boolean => {
    return date.getMonth() === month;
  };


  if (!user) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={
            <div className="page-header-center-date">
              {formatDate(selectedDate)}
              <span className="page-header-date-arrow">▶</span>
            </div>
          }
          onCenterClick={() => setShowCalendarDrawer(!showCalendarDrawer)}
          rightActions={
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowCalendarDrawer(!showCalendarDrawer)}
              title="Select Date"
            >
              📅
            </button>
          }
        />
        <div className="auth-required">
          <p>Please sign in to access your notes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="page-header-center-date">
            {formatDate(selectedDate)}
            <span className="page-header-date-arrow">▶</span>
          </div>
        }
        onCenterClick={() => setShowCalendarDrawer(!showCalendarDrawer)}
        rightActions={
          <div style={{ position: 'relative' }}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowOptionsDrawer(!showOptionsDrawer)}
              title="Options"
            >
              ⋯
            </button>
            {showOptionsDrawer && (
              <div className="options-menu-dropdown">
                <button 
                  className="task-menu-option"
                  onClick={() => {
                    setIsFullBlockMode(!isFullBlockMode);
                    setShowOptionsDrawer(false);
                  }}
                >
                  Full Block Mode: {isFullBlockMode ? 'ON' : 'OFF'}
                </button>
                <div className="task-menu-separator"></div>
                <button 
                  className="task-menu-option"
                  onClick={() => {
                    saveNote();
                    setShowOptionsDrawer(false);
                  }}
                  disabled={saving || !blockManager.blocksToMarkdown().trim()}
                >
                  {saving ? 'Saving...' : 'Save Note'}
                </button>
                <button 
                  className="task-menu-option"
                  onClick={handleMoveUndoneTasksFrom7DaysAgo}
                >
                  Move undone tasks from 7 days ago
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Completion Value Modal */}
      {completionModalState && (
        <CompletionValueModal
          habit={completionModalState.habit}
          date={completionModalState.date}
          currentValue={
            (habitCompletions[completionModalState.habit.id] as HabitCompletion)?.completion_count
          }
          currentMeasurement={
            (habitCompletions[completionModalState.habit.id] as HabitCompletion)?.measurement_value
          }
          onSave={handleSaveCompletionValue}
          onClose={() => setCompletionModalState(null)}
        />
      )}

      {/* Calendar Drawer */}
      {showCalendarDrawer && (
        <div className="calendar-drawer-overlay" onClick={() => setShowCalendarDrawer(false)}>
          <div className="calendar-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Select Date</h3>
              <button className="drawer-close" onClick={() => setShowCalendarDrawer(false)}>×</button>
            </div>
            <div className="drawer-content">
              {/* Calendar Navigation */}
              <div className="calendar-navigation">
                <button 
                  className="calendar-nav-btn"
                  onClick={() => {
                    const newDate = new Date(calendarDate);
                    newDate.setMonth(newDate.getMonth() - 1);
                    setCalendarDate(newDate);
                    loadDatesWithNotes(newDate);
                  }}
                >
                  ‹
                </button>
                <div className="calendar-month-year-container" style={{ position: 'relative' }}>
                  <button
                    className="calendar-month-year-btn"
                    onClick={() => setShowYearMonthSelector(!showYearMonthSelector)}
                  >
                    {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </button>
                  {showYearMonthSelector && (
                    <div className="year-month-selector">
                      <div className="year-month-selector-header">
                        <span>Select Year & Month</span>
                        <button 
                          className="year-month-selector-close"
                          onClick={() => setShowYearMonthSelector(false)}
                        >
                          ×
                        </button>
                      </div>
                      <div className="year-month-selector-content">
                        <div className="year-selector">
                          <label className="year-month-label">Year:</label>
                          <select
                            className="year-month-select"
                            value={calendarDate.getFullYear()}
                            onChange={(e) => {
                              const newDate = new Date(calendarDate);
                              newDate.setFullYear(parseInt(e.target.value));
                              setCalendarDate(newDate);
                              loadDatesWithNotes(newDate);
                            }}
                          >
                            {Array.from({ length: 50 }, (_, i) => {
                              const year = 2000 + i;
                              return (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        <div className="month-selector">
                          <label className="year-month-label">Month:</label>
                          <select
                            className="year-month-select"
                            value={calendarDate.getMonth()}
                            onChange={(e) => {
                              const newDate = new Date(calendarDate);
                              newDate.setMonth(parseInt(e.target.value));
                              setCalendarDate(newDate);
                              loadDatesWithNotes(newDate);
                            }}
                          >
                            {[
                              'January', 'February', 'March', 'April', 'May', 'June',
                              'July', 'August', 'September', 'October', 'November', 'December'
                            ].map((month, index) => (
                              <option key={index} value={index}>
                                {month}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button 
                  className="calendar-nav-btn"
                  onClick={() => {
                    const newDate = new Date(calendarDate);
                    newDate.setMonth(newDate.getMonth() + 1);
                    setCalendarDate(newDate);
                    loadDatesWithNotes(newDate);
                  }}
                >
                  ›
                </button>
              </div>

              {/* Quick Navigation Buttons */}
              <div className="quick-navigation">
                <button
                  className="quick-nav-btn"
                  onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    handleDateChange(today);
                    setShowCalendarDrawer(false);
                  }}
                >
                  Today
                </button>
                <button
                  className="quick-nav-btn"
                  onClick={() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayStr = yesterday.toISOString().split('T')[0];
                    handleDateChange(yesterdayStr);
                    setShowCalendarDrawer(false);
                  }}
                >
                  Yesterday
                </button>
                <button
                  className="quick-nav-btn"
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const tomorrowStr = tomorrow.toISOString().split('T')[0];
                    handleDateChange(tomorrowStr);
                    setShowCalendarDrawer(false);
                  }}
                >
                  Tomorrow
                </button>
              </div>
              
              {/* Full Calendar Grid */}
              <div className="calendar-grid">
                {/* Day headers */}
                <div className="calendar-day-headers">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="calendar-day-header">{day}</div>
                  ))}
                </div>
                
                {/* Calendar days */}
                <div className="calendar-days">
                  {generateCalendarDays(calendarDate.getFullYear(), calendarDate.getMonth()).map((date, index) => {
                    const dateStr = formatCalendarDate(date);
                    const isSelected = dateStr === selectedDate;
                    const isToday = dateStr === today;
                    const hasNote = datesWithNotes.has(dateStr);
                    const isCurrentMonthDate = isCurrentMonth(date, calendarDate.getMonth());
                    
                    return (
                      <button
                        key={index}
                        className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${hasNote ? 'has-note' : ''} ${!isCurrentMonthDate ? 'other-month' : ''}`}
                        onClick={() => {
                          handleDateChange(dateStr);
                          setShowCalendarDrawer(false);
                        }}
                      >
                        {date.getDate()}
                        {hasNote && <div className="note-indicator"></div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Picker Modal for Moving Task */}
      {showDatePickerModal && taskToMove && (
        <div className="calendar-drawer-overlay" onClick={cancelDatePicker}>
          <div className="date-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Move Task to Date</h3>
              <button className="drawer-close" onClick={cancelDatePicker}>×</button>
            </div>
            <div className="drawer-content">
              <div className="date-picker-content">
                <div className="date-picker-task-info">
                  <span className="date-picker-task-title">{taskToMove.title}</span>
                </div>
                <div className="date-selector">
                  <label htmlFor="move-date-input" className="date-selector-label">
                    Select Date:
                  </label>
                  <input
                    id="move-date-input"
                    type="date"
                    value={selectedMoveDate}
                    onChange={(e) => setSelectedMoveDate(e.target.value)}
                    className="date-selector-input"
                  />
                </div>
                <div className="date-picker-actions">
                  <button
                    className="date-picker-cancel-btn"
                    onClick={cancelDatePicker}
                  >
                    Cancel
                  </button>
                  <button
                    className="date-picker-confirm-btn"
                    onClick={handleMoveTaskToDate}
                    disabled={!selectedMoveDate}
                  >
                    Move Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timer Modal for Logging Time */}
      {showTimerModal && taskForTimer && user && (
        <div className="calendar-drawer-overlay" onClick={handleTimerClose}>
          <div className="date-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Log Time for {taskForTimer.title}</h3>
              <button className="drawer-close" onClick={handleTimerClose}>×</button>
            </div>
            <div className="drawer-content">
              <TaskTimer
                task={taskForTimer}
                user={user}
                onTimeLogCreated={handleTimeLogCreated}
                onClose={handleTimerClose}
              />
            </div>
          </div>
        </div>
      )}

      <div className="notes-content">
        
        {/* Sections Toggle Button */}
        <div className="sections-toggle-container">
          <button 
            className="sections-toggle-btn"
            onClick={() => setShowAllSections(!showAllSections)}
          >
            <span className="sections-toggle-arrow">
              {showAllSections ? '▼' : '▶'}
            </span>
          </button>
        </div>


        {/* Habit Tracking Section */}
        {showAllSections && (daysSectionConfig?.habits ?? true) && (
          <HabitSection
            showSection={showHabitsSection}
            onToggleSection={() => setShowHabitsSection(!showHabitsSection)}
            habits={getFilteredHabits()}
            habitCompletions={habitCompletions}
            habitScores={habitScores}
            loading={habitsLoading}
            onToggleCompletion={toggleHabitCompletion}
            onHabitsReload={() => loadHabitsForDate(selectedDate)}
            onError={setError}
            userId={getUserId()}
            showOnlyIncomplete={showOnlyIncompleteHabits}
            onToggleShowOnlyIncomplete={() => setShowOnlyIncompleteHabits(!showOnlyIncompleteHabits)}
          />
        )}

        {/* Schedule Section */}
        {showAllSections && (daysSectionConfig?.schedule ?? false) && (
          <div className="schedule-section-days">
            <button 
              className="schedule-section-toggle-days"
              onClick={() => setShowScheduleSection(!showScheduleSection)}
            >
              <span className="schedule-toggle-text-days">
                /schedule
                {scheduleEvents.length > 0 && (
                  <span className="schedule-count-days">
                    {' - '}
                    {scheduleEvents.length}
                  </span>
                )}
              </span>
              <span className="schedule-toggle-arrow-days">{showScheduleSection ? '▼' : '▶'}</span>
            </button>
            
            <div 
              className={`schedule-section-content-days ${showScheduleSection ? 'expanded' : 'collapsed'}`}
              style={{
                maxHeight: showScheduleSection ? '500px' : '0px',
                paddingTop: showScheduleSection ? '1rem' : '0px',
                paddingBottom: showScheduleSection ? '1rem' : '0px',
              }}
            >
              {scheduleLoading ? (
                <div className="schedule-loading-days">Loading schedule...</div>
              ) : scheduleEvents.length > 0 ? (
                <div className="schedule-list-days">
                  {scheduleEvents.map((event) => {
                    // Format time from HH:MM:SS to HH:MM
                    const timeDisplay = event.event_time.substring(0, 5);
                    
                    // Calculate event status (only for today's date)
                    const { isEventPast, isEventCurrent } = (() => {
                      if (!isToday) return { isEventPast: false, isEventCurrent: false }; // Only check for today
                      
                      const now = new Date();
                      const [hours, minutes] = event.event_time.split(':').map(Number);
                      const startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes || 0);
                      
                      // Calculate end time using duration_minutes (default to 0 if not specified)
                      const durationMinutes = event.duration_minutes || 0;
                      const endTime = new Date(startTime.getTime() + durationMinutes * 60000);
                      
                      // Event is past if current time is after the end time
                      const past = now > endTime;
                      
                      // Event is current if it's between start and end time
                      const current = now >= startTime && now <= endTime;
                      
                      return { isEventPast: past, isEventCurrent: current };
                    })();
                    
                    return (
                      <div 
                        key={event.id} 
                        className={`schedule-item-days ${isEventCurrent ? 'schedule-item-current-days' : ''} ${isEventPast ? 'schedule-item-past-days' : ''}`}
                        title={event.description || event.title}
                      >
                        <div 
                          className="schedule-color-indicator-days" 
                          style={{ backgroundColor: event.color }}
                        ></div>
                        <span className={`schedule-time-days ${isEventCurrent ? 'schedule-time-current-days' : ''} ${isEventPast ? 'schedule-time-past-days' : ''}`}>{timeDisplay}</span>
                        <span className={`schedule-title-days ${isEventCurrent ? 'schedule-title-current-days' : ''} ${isEventPast ? 'schedule-title-past-days' : ''}`}>{event.title}</span>
                        {event.duration_minutes > 0 && (
                          <span className={`schedule-duration-days ${isEventCurrent ? 'schedule-duration-current-days' : ''} ${isEventPast ? 'schedule-duration-past-days' : ''}`}>
                            ({event.duration_minutes} min)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="schedule-empty-days">No events scheduled for this date</div>
              )}
              
              {/* Actions */}
              <div className="schedule-actions-days">
                <button
                  className="schedule-view-all-btn-days"
                  onClick={() => navigate(`/schedule/${selectedDate}`)}
                  title="View full schedule"
                >
                  View Full Schedule
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Project Sections */}
        {showAllSections && activeProjects.map((project) => {
          const projectTasks = projectTaskMap[project.id] || [];
          const isExpanded = expandedProjects.has(project.id);
          
          // Only show project section if it has tasks
          if (projectTasks.length === 0) return null;
          
          return (
            <div key={project.id} className="project-section-days">
              <button 
                className="project-section-toggle-days"
                onClick={() => toggleProjectExpansion(project.id)}
              >
                <span className="project-toggle-text-days">
                  /{project.name}
                  {projectTasks.length > 0 && (
                    <span className="project-count-days">
                      {' - '}
                      {projectTasks.filter(task => task.status === 'completed').length}
                      {' / '}
                      {projectTasks.length}
                    </span>
                  )}
                </span>
                <span className="project-toggle-arrow-days">{isExpanded ? '▼' : '▶'}</span>
              </button>
              
              <div 
                className={`project-section-content-days ${isExpanded ? 'expanded' : 'collapsed'}`}
                style={{
                  maxHeight: isExpanded ? '500px' : '0px',
                  paddingTop: isExpanded ? '1rem' : '0px',
                  paddingBottom: isExpanded ? '1rem' : '0px',
                }}
              >
                {/* Project Actions */}
                <div className="tasks-actions">
                  <button 
                    className="tasks-new-btn"
                    onClick={() => {
                      if (projectNewTaskForm === project.id) {
                        setProjectNewTaskForm(null);
                      } else {
                        setProjectNewTaskForm(project.id);
                      }
                    }}
                    title="Add new task to this project"
                  >
                    + Add Task
                  </button>
                  <button 
                    className="tasks-view-all-btn"
                    onClick={() => navigate(`/projects/${project.id}`)}
                    title="View project details and all tasks"
                  >
                    View project
                  </button>
                </div>

                {/* New Task Form for Project */}
                {projectNewTaskForm === project.id && (
                  <div className="new-task-form">
                    <div className="new-task-form-header">
                      <span className="new-task-form-title">New Task - {project.name}</span>
                      <button 
                        className="new-task-form-close"
                        onClick={() => cancelProjectTaskCreation(project.id)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="new-task-form-content">
                      <input
                        type="text"
                        placeholder="Task title..."
                        value={projectNewTaskTitle[project.id] || ''}
                        onChange={(e) => setProjectNewTaskTitle(prev => ({ ...prev, [project.id]: e.target.value }))}
                        className="new-task-title-input"
                        autoFocus
                      />
                      <textarea
                        placeholder="Description (optional)..."
                        value={projectNewTaskDescription[project.id] || ''}
                        onChange={(e) => setProjectNewTaskDescription(prev => ({ ...prev, [project.id]: e.target.value }))}
                        className="new-task-description-input"
                        rows={2}
                      />
                      <div className="new-task-tags-section">
                        <TagSelector
                          selectedTags={projectNewTaskTags[project.id] || []}
                          onChange={(tags) => setProjectNewTaskTags(prev => ({ ...prev, [project.id]: tags }))}
                          availableTags={availableTags}
                          placeholder="Add tags..."
                        />
                      </div>
                      <div className="new-task-form-actions">
                        <button 
                          className="new-task-cancel-btn"
                          onClick={() => cancelProjectTaskCreation(project.id)}
                          disabled={creatingProjectTask === project.id}
                        >
                          Cancel
                        </button>
                        <button 
                          className="new-task-create-btn"
                          onClick={() => createProjectTask(project.id)}
                          disabled={creatingProjectTask === project.id || !projectNewTaskTitle[project.id]?.trim()}
                        >
                          {creatingProjectTask === project.id ? 'Creating...' : 'Create Task'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {projectTasks.length > 0 ? (
                  <div className="tasks-list">
                    {projectTasks.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        onToggleStatus={toggleTaskCompletion}
                        onTaskClick={handleTaskClick}
                        onMenuAction={handleTaskMenuAction}
                        taskTags={taskTags[task.id] || []}
                        availableTags={availableTags}
                        showMenu={true}
                        showStatusToggle={true}
                        isHighlighted={highlightedTaskIds.has(task.id)}
                        onToggleHighlight={toggleTaskHighlight}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="project-empty-days">No tasks for this project</div>
                )}
              </div>
            </div>
          );
        })}

        {/* Tasks Section */}
        {showAllSections && (daysSectionConfig?.tasks ?? true) && (
          <div className="tasks-section">
          <button 
            className="tasks-section-toggle"
            onClick={() => setShowTasksSection(!showTasksSection)}
          >
            <span className="tasks-toggle-text">
              /tasks
              {generalTasks.length > 0 && (
                <span className="tasks-count">
                  {' - '}
                  {generalTasks.filter(task => task.status === 'completed').length}
                  {' / '}
                  {generalTasks.length}
                </span>
              )}
            </span>
            <span className="tasks-toggle-arrow">{showTasksSection ? '▼' : '▶'}</span>
          </button>
          
          <div 
            className={`tasks-section-content ${showTasksSection ? 'expanded' : 'collapsed'}`}
            style={{
              maxHeight: showTasksSection ? '500px' : '0px',
              paddingTop: showTasksSection ? '1rem' : '0px',
              paddingBottom: showTasksSection ? '1rem' : '0px',
            }}
          >
              {/* Tasks Actions */}
              <div className="tasks-actions">
                <button 
                  className="tasks-new-btn"
                  onClick={() => setShowNewTaskForm(!showNewTaskForm)}
                  title="Add new task"
                >
                  + Add Task
                </button>
                <button 
                  className="tasks-view-all-btn"
                  onClick={() => navigate('/tasks')}
                  title="View all tasks"
                >
                  View All Tasks
                </button>
              </div>

              {/* New Task Form */}
              {showNewTaskForm && (
                <div className="new-task-form">
                  <div className="new-task-form-header">
                    <span className="new-task-form-title">New Task</span>
                    <button 
                      className="new-task-form-close"
                      onClick={cancelTaskCreation}
                    >
                      ×
                    </button>
                  </div>
                  <div className="new-task-form-content">
                    <input
                      type="text"
                      placeholder="Task title..."
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      className="new-task-title-input"
                      autoFocus
                    />
                    <textarea
                      placeholder="Description (optional)..."
                      value={newTaskDescription}
                      onChange={(e) => setNewTaskDescription(e.target.value)}
                      className="new-task-description-input"
                      rows={2}
                    />
                    {projects.length > 0 && (
                      <div className="new-task-project-section">
                        <label htmlFor="new-task-project-select" className="new-task-project-label">Project (optional)</label>
                        <select
                          id="new-task-project-select"
                          value={selectedProjectId || ''}
                          onChange={(e) => setSelectedProjectId(e.target.value ? Number(e.target.value) : undefined)}
                          className="new-task-project-select"
                        >
                          <option value="">No Project</option>
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="new-task-tags-section">
                      <TagSelector
                        selectedTags={selectedTags}
                        onChange={setSelectedTags}
                        availableTags={availableTags}
                        placeholder="Add tags..."
                      />
                    </div>
                    <div className="new-task-form-actions">
                      <button 
                        className="new-task-cancel-btn"
                        onClick={cancelTaskCreation}
                        disabled={creatingTask}
                      >
                        Cancel
                      </button>
                      <button 
                        className="new-task-create-btn"
                        onClick={createNewTask}
                        disabled={creatingTask || !newTaskTitle.trim()}
                      >
                        {creatingTask ? 'Creating...' : 'Create Task'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tasks List */}
              {tasksLoading ? (
                <div className="tasks-loading">Loading tasks...</div>
              ) : generalTasks.length > 0 ? (
                <div className="tasks-list">
                  {generalTasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      onToggleStatus={toggleTaskCompletion}
                      onTaskClick={handleTaskClick}
                      onMenuAction={handleTaskMenuAction}
                      taskTags={taskTags[task.id] || []}
                      availableTags={availableTags}
                      showMenu={true}
                      showStatusToggle={true}
                      isHighlighted={highlightedTaskIds.has(task.id)}
                      onToggleHighlight={toggleTaskHighlight}
                    />
                  ))}
                </div>
              ) : (
                <div className="tasks-empty">No tasks assigned for this date</div>
              )}
          </div>
        </div>
        )}

        {/* Goals Section */}
        {showAllSections && (daysSectionConfig?.goals ?? false) && (
          <div className="goals-section-days">
            <button 
              className="goals-section-toggle-days"
              onClick={() => setShowGoalsSection(!showGoalsSection)}
            >
              <span className="goals-toggle-text-days">
                /goals
                {currentYearGoals.length > 0 && (
                  <span className="goals-count-days">
                    {' - '}
                    {currentYearGoals.length}
                  </span>
                )}
              </span>
              <span className="goals-toggle-arrow-days">{showGoalsSection ? '▼' : '▶'}</span>
            </button>
            
            <div 
              className={`goals-section-content-days ${showGoalsSection ? 'expanded' : 'collapsed'}`}
              style={{
                maxHeight: showGoalsSection ? '500px' : '0px',
                paddingTop: showGoalsSection ? '1rem' : '0px',
                paddingBottom: showGoalsSection ? '1rem' : '0px',
              }}
            >
              {goalsLoading ? (
                <div className="goals-loading-days">Loading goals...</div>
              ) : currentYearGoals.length > 0 ? (
                <div className="goals-list-with-progress">
                  {currentYearGoals.map((goal) => {
                    const progress = goalProgressMap[goal.id] ?? goal.progress ?? 0;
                    const progressColor = getProgressColor(progress);
                    const isCalculating = loadingProgressForGoals.has(goal.id) && goalProgressMap[goal.id] === undefined;
                    
                    return (
                      <div 
                        key={goal.id} 
                        className="goal-item-with-progress"
                        onClick={() => handleGoalClick(goal.id)}
                        title={`${goal.title} - ${isCalculating ? 'Calculating...' : `${progress}% complete`}`}
                      >
                        <span className="goal-name-progress clickable-goal">
                          {goal.title}
                        </span>
                        {isCalculating ? (
                          <span className="goal-progress-loading">
                            <span className="progress-spinner">⟳</span>
                          </span>
                        ) : (
                          <span 
                            className="goal-progress-badge"
                            style={{ 
                              backgroundColor: progressColor,
                              color: progress === 0 ? '#666666' : '#ffffff'
                            }}
                          >
                            {progress}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="goals-empty-days">No goals for this year</div>
              )}
              
              {/* Actions */}
              <div className="goals-actions-days">
                <button
                  className="goals-view-all-btn-days"
                  onClick={() => navigate('/goals')}
                  title="View all goals"
                >
                  View All Goals
                </button>
              </div>
            </div>
          </div>
        )}

        {/* On-going Works Section */}
        {showAllSections && (daysSectionConfig?.onGoingWorks?.enabled ?? false) && (
          <div className="ongoing-works-section-days">
            <button
              className="ongoing-works-section-toggle-days"
              onClick={() => setShowOnGoingWorksSection(!showOnGoingWorksSection)}
            >
              <span className="ongoing-works-toggle-text-days">
                /on-going-works
                {(() => {
                  const totalCount = onGoingNovels.length + favoriteKnowledge.length + onGoingBooks.length + onGoingEssays.length;
                  return totalCount > 0 ? (
                    <span className="ongoing-works-count-days">
                      {' - '}
                      {totalCount}
                    </span>
                  ) : null;
                })()}
              </span>
              <span className="ongoing-works-toggle-arrow-days">{showOnGoingWorksSection ? '▼' : '▶'}</span>
            </button>

            <div
              className={`ongoing-works-section-content-days ${showOnGoingWorksSection ? 'expanded' : 'collapsed'}`}
              style={{
                maxHeight: showOnGoingWorksSection ? '500px' : '0px',
                paddingTop: showOnGoingWorksSection ? '1rem' : '0px',
                paddingBottom: showOnGoingWorksSection ? '1rem' : '0px',
              }}
            >
              {/* Novels Subsection */}
              {(daysSectionConfig?.onGoingWorks?.novels ?? false) && (
                <>
                  {novelsLoading ? (
                    <div className="ongoing-works-loading-days">Loading novels...</div>
                  ) : onGoingNovels.length > 0 ? (
                    <div className="ongoing-works-subsection-days">
                      <div className="ongoing-works-subsection-header-days">Novels</div>
                      <div className="ongoing-works-list-days">
                        {onGoingNovels.map((novel) => (
                          <div
                            key={novel.novel_id}
                            className="ongoing-works-item-days"
                            onClick={() => handleNovelClick(novel.novel_id)}
                          >
                            <span className="ongoing-works-item-name-days">{novel.title}</span>
                            {novel.chapters && novel.chapters.length > 0 && (
                              <span className="ongoing-works-item-count-days">
                                ({novel.chapters.length} {novel.chapters.length === 1 ? 'chapter' : 'chapters'})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              {/* Essays Subsection */}
              {(daysSectionConfig?.onGoingWorks?.essays ?? false) && (
                <>
                  {essaysLoading ? (
                    <div className="ongoing-works-loading-days">Loading essays...</div>
                  ) : onGoingEssays.length > 0 ? (
                    <div className="ongoing-works-subsection-days">
                      <div className="ongoing-works-subsection-header-days">Essays</div>
                      <div className="ongoing-works-list-days">
                        {onGoingEssays.map((essay) => (
                          <div
                            key={essay.essay_id}
                            className="ongoing-works-item-days"
                            onClick={() => navigate(`/essays/${essay.essay_id}`)}
                          >
                            <span className="ongoing-works-item-name-days">{essay.title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              {/* Knowledge Subsection */}
              {(daysSectionConfig?.onGoingWorks?.knowledge ?? false) && (
                <>
                  {knowledgeLoading ? (
                    <div className="ongoing-works-loading-days">Loading knowledge...</div>
                  ) : favoriteKnowledge.length > 0 ? (
                    <div className="ongoing-works-subsection-days">
                      <div className="ongoing-works-subsection-header-days">Knowledge</div>
                      <div className="ongoing-works-list-days">
                        {favoriteKnowledge.map((category) => (
                          <div
                            key={category.category_id}
                            className="ongoing-works-item-days"
                            onClick={() => handleKnowledgeClick(category.category_id)}
                          >
                            <span className="ongoing-works-item-name-days">{category.category_name}</span>
                            {knowledgeLessonCounts[category.category_id] !== undefined && (
                              <span className="ongoing-works-item-count-days">
                                ({knowledgeLessonCounts[category.category_id]})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              {/* Books Subsection */}
              {(daysSectionConfig?.onGoingWorks?.books ?? false) && (
                <>
                  {booksLoading ? (
                    <div className="ongoing-works-loading-days">Loading books...</div>
                  ) : onGoingBooks.length > 0 ? (
                    <div className="ongoing-works-subsection-days">
                      <div className="ongoing-works-subsection-header-days">Books</div>
                      <div className="ongoing-works-list-days">
                        {onGoingBooks.map((book) => (
                          <div
                            key={book.id}
                            className="ongoing-works-item-days"
                            onClick={() => handleBookClick(book.id)}
                          >
                            <div className="ongoing-works-item-content-days">
                              <span className="ongoing-works-item-name-days">{book.title}</span>
                              {book.author && (
                                <span className="ongoing-works-item-author-days">{book.author}</span>
                              )}
                            </div>
                            {book.readingProgress !== undefined && (
                              <span className="ongoing-works-item-count-days">
                                {Math.round(book.readingProgress)}%
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              {/* Empty State */}
              {!novelsLoading && !knowledgeLoading && !booksLoading && !essaysLoading && 
               (daysSectionConfig?.onGoingWorks?.novels ?? false ? onGoingNovels.length === 0 : true) &&
               (daysSectionConfig?.onGoingWorks?.knowledge ?? false ? favoriteKnowledge.length === 0 : true) &&
               (daysSectionConfig?.onGoingWorks?.books ?? false ? onGoingBooks.length === 0 : true) &&
               (daysSectionConfig?.onGoingWorks?.essays ?? false ? onGoingEssays.length === 0 : true) &&
               ((daysSectionConfig?.onGoingWorks?.novels ?? false) || 
                (daysSectionConfig?.onGoingWorks?.knowledge ?? false) || 
                (daysSectionConfig?.onGoingWorks?.books ?? false) ||
                (daysSectionConfig?.onGoingWorks?.essays ?? false)) && (
                <div className="ongoing-works-empty-days">No on-going works found.</div>
              )}

              {/* Actions */}
              <div className="ongoing-works-actions-days">
                <button
                  className="ongoing-works-view-all-btn-days"
                  onClick={() => navigate('/novel')}
                  title="View all novels"
                >
                  View All Novels
                </button>
                <button
                  className="ongoing-works-view-all-btn-days"
                  onClick={() => navigate('/knowledge')}
                  title="View all knowledge"
                >
                  View All Knowledge
                </button>
                <button
                  className="ongoing-works-view-all-btn-days"
                  onClick={() => navigate('/books')}
                  title="View all books"
                >
                  View All Books
                </button>
                <button
                  className="ongoing-works-view-all-btn-days"
                  onClick={() => navigate('/essays')}
                  title="View all essays"
                >
                  View All Essays
                </button>
              </div>
            </div>
          </div>
        )}


        <div className="notes-main">


          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <p>Loading note...</p>
            </div>
          ) : (
            <div className="note-editor">
              <BlockEditorErrorBoundary>
                <BlockEditor
                  blockManager={blockManager}
                  blocks={contentBlocks}
                  placeholder={`What happened on ${formatDate(selectedDate)}? Start typing your thoughts here...`}
                  onSyncStatusChange={(status) => {}} // Sync status is handled by hook
                  fullBlockMode={isFullBlockMode}
                />
              </BlockEditorErrorBoundary>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Days;
